import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  RouterStateSnapshot,
  TitleStrategy,
} from '@angular/router';

@Injectable()
export class RoutePageTitleStrategy extends TitleStrategy {
  private title = inject(Title);

  override updateTitle(snapshot: RouterStateSnapshot): void {
    const rawRouteTitle = this.buildTitle(snapshot)?.trim();
    const routeTitle = rawRouteTitle
      ? this.interpolateTitleTokens(rawRouteTitle, snapshot.root)
      : rawRouteTitle;
    const appName = this.getAppName(snapshot.root) || 'Eneza Web';

    if (routeTitle) {
      this.title.setTitle(`${routeTitle} | ${appName}`);
      return;
    }

    this.title.setTitle(appName);
  }

  private interpolateTitleTokens(titleTemplate: string, route: ActivatedRouteSnapshot): string {
    const params = this.collectRouteParams(route);

    return titleTemplate.replace(/\{\{\s*([\w-]+)\s*\}\}/g, (fullMatch, key: string) => {
      const value = params[key]?.trim();
      return value ? value : fullMatch;
    });
  }

  private getAppName(route: ActivatedRouteSnapshot): string {
    const candidates = this.collectSettings(route);
    const settings = candidates.find(
      (candidate) =>
        typeof candidate?.appName === 'string' && (candidate.appName as string).trim().length > 0,
    );
    return settings?.appName?.trim();
  }

  private collectSettings(route: ActivatedRouteSnapshot): any[] {
    const settings = route.data?.['generalSettings'];
    const current = settings ? [settings] : [];

    if (!route.children?.length) {
      return current;
    }

    return [...current, ...route.children.flatMap((child) => this.collectSettings(child))];
  }

  private collectRouteParams(route: ActivatedRouteSnapshot): Record<string, string> {
    const current = Object.entries(route.params || {}).reduce<Record<string, string>>(
      (accumulator, [key, value]) => {
        if (typeof value === 'string' && value.trim().length > 0) {
          accumulator[key] = value;
        }

        return accumulator;
      },
      {},
    );

    const activeChild =
      route.children?.find((child) => child.outlet === PRIMARY_OUTLET) ||
      route.firstChild ||
      route.children?.[0];

    if (!activeChild) {
      return current;
    }

    return {
      ...current,
      ...this.collectRouteParams(activeChild),
    };
  }
}
